import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiReduxResp } from '~src/services/apiMiddleware';
import { translateContent } from '~src/services/translationServices';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import CustomTextArea from '~src/shared/form/inputs/CustomTextArea';
import TitleInput from '~src/shared/form/inputs/TitleInput';
import { ObjectiveItem, ObjectiveItemUpdate, ObjectiveStatus } from '~src/store/okrs/interfaces';
import SelectorHandler from '~src/modules/okrs/manageObjectives/shared/SelectorHandler';
import { InfoContainer } from '~src/modules/okrs/manageObjectives/shared/shared.sc';

import { ButtonSection, FixedDescription, NewButton, SmallFormSection } from './infoEdit.sc';
import ProgressIndicator from './ProgressIndicator';

interface OwnProps {
  hasChangePermissions: boolean;
  objective: ObjectiveItem;
  update: (values: Partial<ObjectiveItemUpdate>) => ApiReduxResp<ObjectiveItem>;
}

type Props = OwnProps;

const InfoEdit: FC<Props> = props => {
  const [t] = useTranslation();
  const [title, setTitle] = useState(translateContent(props.objective.name));
  const [titleErr, setTitleErr] = useState<string | undefined>(undefined);

  const titleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    if (!e.target.value || e.target.value.length < 1) {
      setTitleErr('required');
    } else {
      setTitleErr(undefined);
    }
  };

  const onTitleBlur = () => {
    if (title && title.length > 0) {
      props.update({ name: { en_US: title } });
    }
  };

  const [showDescriptionForm, setShowDescriptionForm] = useState(false);

  const openDescriptionForm = () => setShowDescriptionForm(true);

  const hideDescriptionForm = () => setShowDescriptionForm(false);

  const [descriptionTextValue, setDescriptionTextValue] = useState(
    translateContent(props.objective.description),
  );

  const onChangeDescriptionTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescriptionTextValue(e.target.value);
  };

  const [loadingSaveDescription, setLoadingSaveDescription] = useState(false);

  const updateDescription = async () => {
    setLoadingSaveDescription(true);
    await props.update({ description: { en_US: descriptionTextValue } });
    setLoadingSaveDescription(false);
    hideDescriptionForm();
  };

  const hasDescription = props.objective.description && Object.keys(props.objective.description).length > 0;

  const getOnBlurCall = (blurFunc: () => void) => () => {
    blurFunc();
    onTitleBlur();
  };

  const isEnabled = props.hasChangePermissions && props.objective.status !== ObjectiveStatus.CLOSED;

  return (
    <InfoContainer $editMode>
      <SelectorHandler hoverTextKey="x.objective.edit.icon.title" iconName="iconTarget">
        {(isSelected, select, blur) => (
          <div data-translation="x.okr.objective.form.title.placeholder">
            <TitleInput
              locked={!isEnabled}
              defaultValue={title}
              placeholder={t('x.okr.objective.form.title.placeholder')}
              onSelect={select}
              onBlur={getOnBlurCall(blur)}
              isSelected={isSelected}
              onChange={titleChange}
              error={titleErr}
            />
          </div>
        )}
      </SelectorHandler>
      <SelectorHandler hoverTextKey="x.objective.add.icon.description" iconName="iconBars">
        {() =>
          showDescriptionForm ? (
            <SmallFormSection>
              <CustomTextArea
                label={t('x.objective.edit.description.label')}
                labelKey="x.objective.edit.description.label"
                onChange={onChangeDescriptionTextArea}
                value={descriptionTextValue}
              />
              <ButtonSection>
                <SecondaryButton
                  data-translation="x.okr.edit.description.button.cancel"
                  onClick={hideDescriptionForm}
                >
                  {t('x.okr.edit.description.button.cancel')}
                </SecondaryButton>
                <PrimaryButtonLoading
                  data-translation="x.okr.edit.description.button.save"
                  onClick={updateDescription}
                  isLoading={loadingSaveDescription}
                >
                  {t('x.okr.edit.description.button.save')}
                </PrimaryButtonLoading>
              </ButtonSection>
            </SmallFormSection>
          ) : (
            <FixedDescription onClick={isEnabled ? openDescriptionForm : undefined}>
              {hasDescription ? (
                <span>{translateContent(props.objective.description)}</span>
              ) : (
                isEnabled && (
                  <NewButton data-translation="x.okr.edit.description.button.new" className="button_text">
                    {t('x.okr.edit.description.button.new')}
                  </NewButton>
                )
              )}
            </FixedDescription>
          )
        }
      </SelectorHandler>
      <ProgressIndicator progress={props.objective.progress} />
    </InfoContainer>
  );
};

export default InfoEdit;
